/* eslint-disable no-nested-ternary */
import type { RatingResponse } from '../../repositories';
import { PriceVO } from '../price';
import { PromoCodeVO } from '../promo-code';

type Addon = Rating['parsedApiResponse']['addOnRating'][0];
export class Rating {
  // eslint-disable-next-line no-use-before-define
  private parsedApiResponse: ReturnType<Rating['parseApiResponse']>;

  constructor(private response: RatingResponse) {
    this.parsedApiResponse = this.parseApiResponse(response);
  }

  public getCurrency() {
    return this.parsedApiResponse.ratePlans[0].currentTier.currencyCode;
  }

  public getPaymentPeriod() {
    return this.parsedApiResponse.paymentPeriod;
  }

  // returns plan total price  (includes prepay discounts, does not include discount codes)
  public getPlanTotal() {
    return this.parsedApiResponse.monthlyPrice.multiply(this.parsedApiResponse.paymentPeriod);
  }

  public getTotalTax() {
    return this.parsedApiResponse.totalTaxPrice;
  }

  // returns plan base montly price (does not include any prepay discounts or discount codes)
  public getPlanBaseMonthly() {
    return this.getPlan().currentTier.price;
  }

  // returns plan total base price (does not include any prepay discounts or discount codes)
  public getPlanBaseTotal() {
    return this.getPlan().currentTier.price.multiply(this.getPaymentPeriod());
  }

  // returns all addons total price (includes prepay discounts, does not include discount codes)
  public getAddOnsTotal() {
    return this.parsedApiResponse.addOnRating.reduce(
      (accumulator, current) => accumulator.add(current.monthlyCost.multiply(this.parsedApiResponse.paymentPeriod)),
      new PriceVO(0)
    );
  }

  public getAddOnsTotalProRated() {
    return this.parsedApiResponse.addOnRating.reduce(
      (accumulator, current) => accumulator.add(current.proRatedTotal),
      new PriceVO(0)
    );
  }

  // returns all addons monthly total price (includes prepay discounts, does not include discount codes)
  public getAddOnsMontlyTotal() {
    return this.parsedApiResponse.addOnRating.reduce(
      (accumulator, current) => accumulator.add(current.monthlyCost),
      new PriceVO(0)
    );
  }

  // returns the array of IDs for existing addons on the account
  public getExistingAddOnServices() {
    return this.parsedApiResponse.existingAddOnServices || [];
  }

  public getAppliedDiscountCodes(): PromoCodeVO[] {
    return (
      (this.parsedApiResponse.promotionData || [])
        // consider only applied discount codes
        .filter((discountCode) => discountCode.applied)
        // sort promotionData to make sure that plan promotion (the one with baasServiceId === null) is first element of array
        .sort((a, b) => (a.baasServiceId === null ? -1 : b.baasServiceId === null ? 1 : 0))
        .map((discountCode) => new PromoCodeVO(discountCode))
    );
  }

  public getNewAppliedDiscountCodes(): PromoCodeVO[] {
    return this.getAppliedDiscountCodes().filter((promoCodeVO) => !promoCodeVO.isActive());
  }

  public getPlanAppliedDiscountCodes() {
    return (
      (this.parsedApiResponse.promotionData || [])
        // consider only applied discount codes
        .filter((discountCode) => discountCode.applied)
        // get only plan discount codes (the one with baasServiceId === null)
        .filter((discountCode) => discountCode.baasServiceId === null)
    );
  }

  // returns all applied discount codes total (excludes non-discount promo codes)
  public getAppliedDiscountCodesTotal() {
    return this.getAppliedDiscountCodes()
      .filter((promoCodeVO) => !promoCodeVO.isNonDiscount())
      .reduce((accumulator, promoCodeVO) => accumulator.add(promoCodeVO.getPromotionCredit()), new PriceVO(0));
  }

  // returns total for all discount codes applied to plan (excludes non-discount promo codes)
  public getPlanAppliedDiscountCodesTotal() {
    return this.getAppliedDiscountCodes()
      .filter((promoCodeVO) => promoCodeVO.getBaasServiceId() === null && !promoCodeVO.isNonDiscount())
      .reduce((accumulator, promoCodeVO) => accumulator.add(promoCodeVO.getPromotionCredit()), new PriceVO(0));
  }

  /**
   * List the promo codes that failed to apply during the previous rating call.
   */
  public getUnappliedPromoCodes() {
    return (this.parsedApiResponse.promotionData || []).filter((discountCode) => !discountCode.applied);
  }

  /**
   * List the promo codes that failed to apply during the previous rating call.
   *
   * This is used to remove the failed promo codes from subsequent rating call requests.
   */

  // returns prepay discount for given addon
  public getAddOnPrepayDiscount(addon: Addon) {
    return addon.monthlyDiscount.multiply(this.parsedApiResponse.paymentPeriod);
  }

  // returns monthly cost  for given addon
  public getAddOnMonthlyBaseCost(addon: Addon) {
    return addon.listPrice;
  }

  // returns monthly tax for given addon
  public getAddOnMonthlyTax(addon: Addon) {
    return addon.monthlyTax;
  }

  // returns monthly discount for given addon
  public getAddOnMonthlyDiscount(addon: Addon) {
    return addon.monthlyDiscount;
  }

  // returns total prepay discount for all addons
  public getAllAddOnsPrepayDiscount() {
    return this.parsedApiResponse.addOnRating.reduce(
      (accumulator, current) => accumulator.add(this.getAddOnPrepayDiscount(current)),
      new PriceVO(0)
    );
  }

  // returns total tax for all addons
  public getAllAddOnsMonthlyTax() {
    return this.parsedApiResponse.addOnRating.reduce(
      (accumulator, current) => accumulator.add(this.getAddOnMonthlyTax(current)),
      new PriceVO(0)
    );
  }

  // returns total tax for all addons
  public getAllAddOnsMonthlyBaseCost() {
    return this.parsedApiResponse.addOnRating.reduce(
      (accumulator, current) => accumulator.add(this.getAddOnMonthlyBaseCost(current)),
      new PriceVO(0)
    );
  }

  public getAllAddOnsMonthlyDiscount() {
    return this.parsedApiResponse.addOnRating.reduce(
      (accumulator, current) => accumulator.add(this.getAddOnMonthlyDiscount(current)),
      new PriceVO(0)
    );
  }

  // returns if user is saving anything on their order (prepay discount or applied discount codes).
  public hasSavings() {
    return this.getSavingsTotal().priceAmount > 0;
  }

  // returns prepay discount for plan
  public getPlanPrepayDiscount() {
    return this.parsedApiResponse.prepayTotalAmountSaved;
  }

  // returns all savings (prepay discount for plan and add-ons and all applied discount codes)
  public getSavingsTotal() {
    return this.getPlanPrepayDiscount().add(this.getAllAddOnsPrepayDiscount()).add(this.getAppliedDiscountCodesTotal());
  }

  public getAmountToCharge() {
    return this.parsedApiResponse.amountToCharge;
  }

  public getProratedPrice() {
    return this.parsedApiResponse.proratedPrice;
  }

  public getProratedTotalPrice() {
    return this.parsedApiResponse.proratedTotalPrice;
  }

  public getProratedPromotionDiscountAmount() {
    return this.parsedApiResponse.proratedPromotionDiscountAmount;
  }

  public getProratedPrepayDiscountAmount() {
    return this.parsedApiResponse.proratedPrepayDiscountAmount;
  }

  public getMonthlyBasePrice() {
    // monthlyPrice includes prepay discount for plan, so we need to add it back to get the base price
    return this.parsedApiResponse.monthlyPrice
      .add(this.getPrepayMonthlyDiscountAmount())
      .add(this.getAllAddOnsMonthlyBaseCost());
  }

  public getMonthlyTaxPrice() {
    return this.parsedApiResponse.monthlyTaxPrice.add(this.getAllAddOnsMonthlyTax());
  }

  public getPrepayMonthlyDiscountAmount() {
    return this.parsedApiResponse.prepayMonthlyDiscountAmount;
  }

  public getMonthlyDiscountPrice() {
    return this.getPlanAppliedDiscountCodesTotal()
      .add(this.getPrepayMonthlyDiscountAmount())
      .add(this.getAllAddOnsMonthlyDiscount());
  }

  public getMonthlyTotalPrice() {
    return this.getMonthlyBasePrice().add(this.getMonthlyTaxPrice()).substract(this.getMonthlyDiscountPrice());
  }

  public getAmountAppliedFromBalance() {
    return this.parsedApiResponse.amountAppliedFromBalance;
  }

  public getStartingBalance() {
    return this.parsedApiResponse.startingBalance;
  }

  public getFinalBalance() {
    return this.parsedApiResponse.finalBalance;
  }

  public getBalanceDue() {
    return this.parsedApiResponse.balanceDue;
  }

  public getOverdueAmount() {
    const isOverdueBalanceIncuded = this.getBalanceDue().priceAmount < 0 && this.getFinalBalance().priceAmount === 0;

    if (!isOverdueBalanceIncuded) {
      return null;
    }

    // If the balance is overdue, rating.getBalanceDue() will be negative number, but we want to rerurn it as positive value (it will be "added" to the total)
    const overdueAmount = new PriceVO(Math.abs(this.getBalanceDue().priceAmount));

    return overdueAmount;
  }

  public getIsBalanceCoveringNextPayment() {
    return this.getMonthlyTotalPrice().priceAmount < this.getFinalBalance().priceAmount;
  }

  public getBalanceContributionToNextPayment() {
    return this.getIsBalanceCoveringNextPayment() ? this.getMonthlyTotalPrice() : this.getFinalBalance();
  }

  public getBalanceAfterNextPayment() {
    return this.getIsBalanceCoveringNextPayment()
      ? this.getFinalBalance().substract(this.getMonthlyTotalPrice())
      : new PriceVO(0);
  }

  public getNextPaymentTotalAfterBalanceContribution() {
    return this.getIsBalanceCoveringNextPayment()
      ? new PriceVO(0)
      : this.getMonthlyTotalPrice().substract(this.getBalanceContributionToNextPayment());
  }

  public getPlan() {
    return this.parsedApiResponse.ratePlans[0];
  }

  public getPlanName() {
    // BE returns "Constant Contact - Standard", but we only want "Standard"
    // and for Social Starter plans, we want to return the plan name without the "Social " prefix
    return this.getPlan().planTypeItem.description.replace('Constant Contact - ', '').replace('Social ', '');
  }

  public getAddons() {
    return this.parsedApiResponse.addOnRating;
  }

  public getApiResponse(): RatingResponse {
    return this.response;
  }

  public getStripeQuoteId() {
    return this.parsedApiResponse.stripeQuoteId;
  }

  // TODO - parseApiResponse should be refactored, to imporve readability and reduce complexity for easier future maintenance.
  private parseApiResponse = (apiResponse: RatingResponse) => {
    return {
      ...apiResponse,
      ratePlans: apiResponse.ratePlans.map((ratePlan) => ({
        ...ratePlan,
        currentTier: { ...ratePlan.currentTier, price: new PriceVO(ratePlan.currentTier.price) },
      })),
      addOnRating: (apiResponse.addOnRating || []).map((addon) => ({
        ...addon,
        listPrice: new PriceVO(addon.listPrice),
        monthlyCost: new PriceVO(addon.monthlyCost),
        monthlyDiscount: new PriceVO(addon.monthlyDiscount),
        monthlyTax: new PriceVO(addon.monthlyTax),
        monthlyProRatedPrice: addon.monthlyProRatedPrice === null ? null : new PriceVO(addon.monthlyProRatedPrice),
        monthlyProRatedTax: addon.monthlyProRatedTax === null ? null : new PriceVO(addon.monthlyProRatedTax),
        proRatedPrice: new PriceVO(addon.proRatedPrice),
        proRatedTax: new PriceVO(addon.proRatedTax),
        proRatedTotal: new PriceVO(addon.proRatedTotal),
        totalTax: new PriceVO(addon.totalTax),
        totalPrice: new PriceVO(addon.totalPrice),
        termTotal: new PriceVO(addon.termTotal),
        termTax: new PriceVO(addon.termTax),
        subTotal: new PriceVO(addon.subTotal),
        fullPrice: new PriceVO(addon.fullPrice),
        summarizedTermTotal: new PriceVO(addon.summarizedTermTotal),
      })),
      promotionData: Object.values(apiResponse.promotionData || {}).map((promotion) => ({
        ...promotion,
        proratedPromotionCredit: new PriceVO(promotion.proratedPromotionCredit ?? 0),
        promotionCredit: new PriceVO(promotion.promotionCredit ?? 0),
      })),
      monthlyPrice: new PriceVO(apiResponse.monthlyPrice),
      monthlyTaxPrice: new PriceVO(apiResponse.monthlyTaxPrice),
      prepayMonthlyDiscountAmount: new PriceVO(apiResponse.prepayMonthlyDiscountAmount),
      prepayTotalAmountSaved: new PriceVO(apiResponse.prepayTotalAmountSaved),
      oneTimePrice: new PriceVO(apiResponse.oneTimePrice),
      oneTimeTaxPrice: new PriceVO(apiResponse.oneTimeTaxPrice),
      totalPrice: new PriceVO(apiResponse.totalPrice),
      totalTaxPrice: new PriceVO(apiResponse.totalTaxPrice),
      proratedTotalPrice: new PriceVO(apiResponse.proratedTotalPrice),
      proratedTotalTaxPrice: new PriceVO(apiResponse.proratedTotalTaxPrice),
      proratedPrepayDiscountAmount: new PriceVO(apiResponse.proratedPrepayDiscountAmount),
      proratedPrice: new PriceVO(apiResponse.proratedPrice),
      proratedTaxPrice: new PriceVO(apiResponse.proratedTaxPrice),
      startingBalance: new PriceVO(apiResponse.startingBalance),
      balanceDue: new PriceVO(apiResponse.balanceDue),
      amountAppliedFromBalance: new PriceVO(apiResponse.amountAppliedFromBalance),
      amountIncludedFromRemainingBalance: new PriceVO(apiResponse.amountIncludedFromRemainingBalance),
      amountToCharge: new PriceVO(apiResponse.amountToCharge),
      finalBalance: new PriceVO(apiResponse.finalBalance),
      lastInvoiceAmount: apiResponse.lastInvoiceAmount === null ? null : new PriceVO(apiResponse.lastInvoiceAmount),
      paymentLessTax: apiResponse.paymentLessTax === null ? null : new PriceVO(apiResponse.paymentLessTax),
    };
  };
}
